import { useCallback, useState } from 'react'

export type ConfirmDialogConfig = {
  title: string
  description?: string
  tone: 'warning' | 'danger'
  onConfirm: () => void | Promise<void>
}

export type UseConfirmDialogResult = {
  isOpen: boolean
  config: ConfirmDialogConfig | null
  actionError: string | null
  open: (config: ConfirmDialogConfig) => void
  close: () => void
  confirm: () => Promise<void>
  setActionError: (message: string | null) => void
}

export function useConfirmDialog(): UseConfirmDialogResult {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [config, setConfig] = useState<ConfirmDialogConfig | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const open = useCallback((next: ConfirmDialogConfig) => {
    setConfig(next)
    setActionError(null)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setConfig(null)
    setActionError(null)
  }, [])

  const confirm = useCallback(async () => {
    if (!config) return
    try {
      await config.onConfirm()
      setIsOpen(false)
      setConfig(null)
      setActionError(null)
    } catch {
      return
    }
  }, [config])

  return { isOpen, config, actionError, open, close, confirm, setActionError }
}
